import type { Supplier } from '../../../types'
import { supabase } from '../../../services/supabase'

export type CubeParentSpend = {
  parentName: string
  spend: number
}

export type BriefCubeSpend = {
  parents: CubeParentSpend[]
  byYear: { year: number; spend: number }[]
}

const PAGE = 1000
const MAX_ROWS = 20000

function chunks<T>(items: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size))
  return out
}

/**
 * Invoice spend from this company's cube for suppliers in the brief.
 * Lines match a supplier id or a parent / legal name. Shared spend_k is not used.
 */
export async function cubeSpendForBrief(
  companyId: string,
  rows: Supplier[]
): Promise<BriefCubeSpend> {
  const ids = [...new Set(rows.map((row) => row.id).filter(Boolean))]
  const keys = new Set<string>()
  for (const row of rows) {
    const name = (row.name || '').trim().toLowerCase()
    const parent = (row.parent_name || '').trim().toLowerCase()
    if (name) keys.add(name)
    if (parent) keys.add(parent)
  }

  const seen = new Set<string>()
  const totals = new Map<string, CubeParentSpend>()
  const yearTotals = new Map<number, number>()

  async function absorb(apply: (query: ReturnType<typeof baseQuery>) => ReturnType<typeof baseQuery>) {
    let from = 0
    for (;;) {
      const { data, error } = await apply(baseQuery(companyId)).range(from, from + PAGE - 1)
      if (error) throw error
      const batch = data ?? []
      for (const line of batch) {
        const id = String(line.id)
        if (seen.has(id)) continue
        seen.add(id)
        const parentName = String(line.parent_name || '').trim()
        const key = parentName.toLowerCase()
        if (!key) continue
        const spend = Number(line.spend) || 0
        const prev = totals.get(key)
        if (prev) prev.spend += spend
        else totals.set(key, { parentName, spend })
        const year = line.invoice_year == null ? null : Number(line.invoice_year)
        if (year != null && Number.isFinite(year)) {
          yearTotals.set(year, (yearTotals.get(year) ?? 0) + spend)
        }
      }
      if (batch.length < PAGE || seen.size >= MAX_ROWS) break
      from += PAGE
    }
  }

  for (const idChunk of chunks(ids, 60)) {
    await absorb((query) => query.in('supplier_id', idChunk))
  }
  for (const keyChunk of chunks([...keys], 60)) {
    await absorb((query) => query.in('parent_name_key', keyChunk))
  }

  return {
    parents: [...totals.values()].filter((row) => row.spend !== 0).sort((a, b) => b.spend - a.spend),
    byYear: [...yearTotals.entries()]
      .map(([year, spend]) => ({ year, spend }))
      .filter((row) => row.spend !== 0)
      .sort((a, b) => a.year - b.year),
  }
}

function baseQuery(companyId: string) {
  return supabase
    .from('company_spend_lines')
    .select('id, parent_name, spend, invoice_year')
    .eq('company_id', companyId)
}
